<script setup lang="ts">
import MemberHeader from "@/components/member/MemberHeader.vue";
import DeveloperSidebar from "@/components/developer/DeveloperSidebar.vue";

const validations = [
  {
    id: 'VAL-2024-007',
    project: 'Solar Park Beta – Northern Highlands',
    pddRef: 'PDD-2024-0031',
    doe: 'Bureau Veritas Carbon Assurance',
    doeContact: 'j.walker@bureauveritas.com',
    assignedDate: '2024-03-01',
    dueDate: '2024-05-15',
    status: 'In Progress',
    statusColor: 'blue',
    stage: 'Desk Review',
    stageNumber: 2,
    comments: [
      { date: '2024-04-10', from: 'Bureau Veritas', text: 'Desk review completed. Site visit scheduled for 22 April.' },
      { date: '2024-03-20', from: 'Bureau Veritas', text: 'PDD received and assigned to lead validator.' },
    ],
  },
  {
    id: 'VAL-2024-003',
    project: 'Mangrove Restoration Initiative – Borneo',
    pddRef: 'PDD-2024-0042',
    doe: 'SGS Carbon Services',
    doeContact: 'carbon@sgs.com',
    assignedDate: '2024-01-15',
    dueDate: '2024-03-30',
    status: 'Clarification Requested',
    statusColor: 'yellow',
    stage: 'Clarification Round',
    stageNumber: 3,
    comments: [
      { date: '2024-03-12', from: 'SGS Carbon', text: 'Clarification required: Please provide updated FPIC documentation for communities in Zone 3.' },
      { date: '2024-02-28', from: 'SGS Carbon', text: 'Site visit completed. Report under preparation.' },
    ],
  },
]

const stages = ['PDD Submitted', 'Desk Review', 'Site Visit', 'Clarification Round', 'Validation Report', 'Complete']

const statusClass = (color: string) => {
  const map: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
  }
  return map[color] ?? 'bg-gray-100 text-gray-600'
}
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <DeveloperSidebar />

    <main class="flex-1 overflow-auto py-8">
      <div class="max-w-5xl mx-auto px-5">

        <!-- Breadcrumb -->
        <nav class="text-sm text-gray-500 mb-4">
          <span class="hover:text-primary cursor-pointer">Dashboard</span>
          <span class="mx-2">/</span>
          <span class="text-gray-800 font-medium">DOE Validation Status</span>
        </nav>

        <!-- Header -->
        <section class="mb-8">
          <div class="bg-white rounded-lg border border-gray-200 p-6">
            <h1 class="text-3xl font-bold text-accent mb-2">DOE Validation Status</h1>
            <p class="text-gray-600">
              Track your Project Design Document (PDD) validations conducted by Designated Operational Entities (DOEs). Validation must be successfully completed before your project can be registered.
            </p>
          </div>
        </section>

        <!-- Validation cards -->
        <section class="space-y-6">
          <div
            v-for="val in validations"
            :key="val.id"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <!-- Card header -->
            <div class="flex items-start justify-between mb-6">
              <div>
                <div class="flex items-center space-x-3 mb-1">
                  <h3 class="font-semibold text-accent">{{ val.project }}</h3>
                  <span :class="['inline-flex px-2 py-1 rounded-full text-xs font-medium', statusClass(val.statusColor)]">
                    {{ val.status }}
                  </span>
                </div>
                <p class="text-sm text-gray-500">PDD Ref: {{ val.pddRef }} &nbsp;·&nbsp; Validation Ref: {{ val.id }}</p>
              </div>
              <div class="text-right text-sm text-gray-500">
                <p>Due date</p>
                <p class="font-semibold text-gray-700">{{ val.dueDate }}</p>
              </div>
            </div>

            <!-- Progress steps -->
            <div class="flex items-center mb-6 overflow-x-auto">
              <div
                v-for="(stage, idx) in stages"
                :key="stage"
                class="flex items-center"
              >
                <div class="flex flex-col items-center min-w-[80px]">
                  <div :class="[
                    'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mb-1',
                    idx + 1 < val.stageNumber ? 'bg-green-500 text-white' :
                    idx + 1 === val.stageNumber ? 'bg-primary text-white' :
                    'bg-gray-200 text-gray-500'
                  ]">
                    <i v-if="idx + 1 < val.stageNumber" class="fas fa-check text-xs"></i>
                    <span v-else>{{ idx + 1 }}</span>
                  </div>
                  <span class="text-xs text-center text-gray-600 leading-tight" style="max-width: 72px;">{{ stage }}</span>
                </div>
                <div v-if="idx < stages.length - 1" :class="[
                  'h-0.5 w-8 mx-1 mb-5',
                  idx + 1 < val.stageNumber ? 'bg-green-500' : 'bg-gray-200'
                ]"></div>
              </div>
            </div>

            <!-- DOE info -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-4">
              <div>
                <p class="text-gray-500">DOE</p>
                <p class="font-medium text-gray-800">{{ val.doe }}</p>
              </div>
              <div>
                <p class="text-gray-500">DOE Contact</p>
                <p class="font-medium text-gray-800">{{ val.doeContact }}</p>
              </div>
              <div>
                <p class="text-gray-500">Assigned Date</p>
                <p class="font-medium text-gray-800">{{ val.assignedDate }}</p>
              </div>
            </div>

            <!-- Comments -->
            <div class="space-y-2 mb-4">
              <p class="text-sm font-medium text-gray-700">DOE Comments</p>
              <div
                v-for="comment in val.comments"
                :key="comment.date"
                class="bg-gray-50 rounded-md px-4 py-3 text-sm"
              >
                <p class="text-gray-500 text-xs mb-1">{{ comment.from }} · {{ comment.date }}</p>
                <p class="text-gray-700">{{ comment.text }}</p>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center space-x-3">
              <button class="px-4 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50">
                <i class="fas fa-file-alt mr-1"></i>View PDD
              </button>
              <button v-if="val.statusColor === 'yellow'" class="px-4 py-1.5 bg-primary text-white rounded-md text-sm hover:bg-primary/90">
                <i class="fas fa-upload mr-1"></i>Upload Clarification Response
              </button>
              <button v-if="val.status === 'Complete'" class="px-4 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">
                <i class="fas fa-download mr-1"></i>Download Validation Report
              </button>
            </div>
          </div>
        </section>

      </div>
    </main>
  </div>
</template>
